import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import type { BillingPlanId, PlanTier } from "../types/billing";
import {
  clearPendingCheckoutPlan,
  clearPlanQueryFromUrl,
  getCheckoutPlanFromSearch,
  getPendingCheckoutPlan,
  setPendingCheckoutPlan,
} from "../utils/pending-checkout-plan";

interface UseAutoProCheckoutOptions {
  planTier?: PlanTier;
  betaEnabled?: boolean;
  isPlanLoading: boolean;
  openCheckout: (planId?: BillingPlanId) => Promise<boolean>;
}

const hasRequestedPlan = (
  currentPlan: PlanTier | undefined,
  requestedPlan: BillingPlanId
) => {
  if (requestedPlan === "agency_monthly") {
    return currentPlan === "agency";
  }

  return currentPlan === "pro" || currentPlan === "agency";
};

const useAutoProCheckout = ({
  planTier,
  betaEnabled = false,
  isPlanLoading,
  openCheckout,
}: UseAutoProCheckoutOptions) => {
  const location = useLocation();
  const attemptedPlanRef = useRef<string | null>(null);

  useEffect(() => {
    const planFromSearch = getCheckoutPlanFromSearch(location.search);
    if (planFromSearch) {
      setPendingCheckoutPlan(planFromSearch);
    }
  }, [location.search]);

  useEffect(() => {
    const requestedPlan =
      getCheckoutPlanFromSearch(location.search) ?? getPendingCheckoutPlan();

    if (!requestedPlan || isPlanLoading) {
      return;
    }

    if (betaEnabled) {
      clearPendingCheckoutPlan();
      clearPlanQueryFromUrl();
      return;
    }

    if (hasRequestedPlan(planTier, requestedPlan)) {
      clearPendingCheckoutPlan();
      clearPlanQueryFromUrl();
      return;
    }

    if (attemptedPlanRef.current === requestedPlan) {
      return;
    }

    attemptedPlanRef.current = requestedPlan;

    let cancelled = false;

    void (async () => {
      const opened = await openCheckout(requestedPlan);
      if (cancelled) {
        return;
      }

      if (opened) {
        clearPendingCheckoutPlan();
        clearPlanQueryFromUrl();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [betaEnabled, isPlanLoading, location.search, openCheckout, planTier]);
};

export default useAutoProCheckout;
